import { useEffect, useState } from "react";
import { api, EvidenceBadge, PILLARS } from "@/lib/api";
import { Sparkles, TrendingUp, CheckCircle2, AlertCircle, Rocket, Target } from "lucide-react";

export default function CommandCentre() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/command_centre").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="p-8 text-neutral-500">Loading Command Centre…</div>;

  const readinessColor = data.readiness_score >= 70 ? "#D4FF00" : data.readiness_score >= 40 ? "#FB923C" : "#EF4444";

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Command Centre</div>
        <h1 className="text-5xl font-black tracking-tight leading-none" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>What ships next.</h1>
        <p className="text-sm text-neutral-600 mt-2">Every recommendation below is linked to evidence. Missing data is labelled unavailable, not zero.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white border border-neutral-200 rounded-lg p-6" data-testid="readiness-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">
            <Target className="w-3.5 h-3.5" strokeWidth={2} />Monthly readiness
          </div>
          <div className="text-6xl font-black tracking-tighter" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif", color: readinessColor }}>{data.readiness_score}</div>
          <div className="text-xs text-neutral-500 mt-1">out of 100</div>
          <div className="mt-4 space-y-1.5">
            {Object.entries(data.readiness_checks).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-xs">
                {v ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" strokeWidth={2} /> : <AlertCircle className="w-3.5 h-3.5 text-orange-500" strokeWidth={2} />}
                <span className="text-neutral-700">{k.replace(/_/g, " ")}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-neutral-200 rounded-lg p-6 md:col-span-2" data-testid="next-post-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />Recommended next post
          </div>
          {data.next_post ? (
            <>
              <div className="text-2xl font-bold tracking-tight leading-snug" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{data.next_post.title || data.next_post.working_title}</div>
              <div className="text-sm text-neutral-600 mt-2">{data.next_post.hook || data.next_post.recommended_hook || data.next_post.premise || ""}</div>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <EvidenceBadge confidence={data.next_post.confidence || "medium"} />
                {data.next_post.pillar && <span className="text-xs text-neutral-600 uppercase tracking-wider">{PILLARS[data.next_post.pillar] || data.next_post.pillar}</span>}
                {data.next_post.publish_date && <span className="text-xs text-neutral-500">· {data.next_post.publish_date}</span>}
              </div>
            </>
          ) : (
            <div className="text-sm text-neutral-500">No recommendation yet. Generate ideas or a monthly plan.</div>
          )}
        </div>

        <div className="bg-white border border-neutral-200 rounded-lg p-6" data-testid="progress-card">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">This month</div>
          <div className="text-5xl font-black tracking-tighter" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            {data.month_progress.published}<span className="text-neutral-300">/{data.month_progress.target}</span>
          </div>
          <div className="text-xs text-neutral-500 mt-1">published · {data.month_progress.planned} planned</div>
          <div className="mt-4 h-1.5 bg-neutral-100 rounded overflow-hidden">
            <div className="h-full" style={{ background: "#D4FF00", width: `${Math.min(100, (data.month_progress.published / data.month_progress.target) * 100)}%` }} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <MetricCard label="Ideas in inbox" value={data.ideas_count} testid="stat-ideas" />
        <MetricCard label="Approved scripts" value={data.approved_scripts} testid="stat-scripts" />
        <MetricCard label="Pending approvals" value={data.pending_approvals} testid="stat-pending" />
        <MetricCard label="Trend alerts" value={data.trend_alerts} testid="stat-trends" />
      </div>

      {data.analytics_summary && !data.analytics_summary.has_data && (
        <div className="mt-6 bg-white border border-neutral-200 border-dashed rounded-lg p-6">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-neutral-500 mt-0.5" strokeWidth={1.5} />
            <div>
              <div className="font-semibold text-neutral-900">Insufficient first-party data</div>
              <p className="text-sm text-neutral-600 mt-1">Import Instagram Insights CSV or connect the API. Until then, all recommendations are labelled <span className="font-semibold">Experimental</span>.</p>
            </div>
          </div>
        </div>
      )}

      {data.upcoming_milestones?.length > 0 && (
        <div className="mt-6 bg-white border border-neutral-200 rounded-lg p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-3">
            <Rocket className="w-3.5 h-3.5" strokeWidth={2} />Upcoming milestones
          </div>
          <div className="space-y-2">
            {data.upcoming_milestones.map((m) => (
              <div key={m.id} className="flex items-center justify-between text-sm">
                <div className="font-medium">{m.title}</div>
                <div className="text-neutral-500 text-xs">{m.date}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, testid }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-6" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{label}</div>
      <div className="text-4xl font-black tracking-tighter mt-2" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{value ?? "—"}</div>
    </div>
  );
}
